import type {
  DismissalReason,
  FeedbackDraftView,
  SendFeedbackDraftRequest,
  SendFeedbackResponse,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { csrf } from "../account/queries.ts";
import { api } from "../api.ts";
import { apiError, noContent, ok } from "../api-errors.ts";
import { useSession } from "../session.tsx";
import { FeedbackThrottled, feedbackKeys } from "./queries.ts";

/**
 * Feedback drafts from a conversation (openspec change `feedback-from-conversation`): the
 * caller's drafts of one conversation, "Report a problem" on an answer, and the card's Send and
 * Dismiss. Only the user's click sends; nothing here runs on its own.
 */

export const feedbackDraftKeys = {
  conversation: (conversationId: string) => ["feedback-drafts", conversationId] as const,
};

/** The caller's drafts of a conversation (none while feedback is off, or on an N-1 API). */
export function useFeedbackDrafts(conversationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: feedbackDraftKeys.conversation(conversationId ?? ""),
    enabled: enabled && conversationId !== undefined,
    queryFn: async (): Promise<FeedbackDraftView[]> => {
      const res = await api.api.feedback.drafts.$get({
        query: { conversationId: conversationId ?? "" },
      });
      // An API without drafts (N-1, canary) has no such route: no card.
      if ((res.status as number) === 404) {
        return [];
      }
      return (await ok(res)).drafts as FeedbackDraftView[];
    },
  });
}

/** Keeps a draft's new state in the conversation's cache (a reopened chat shows it settled). */
function useRemember(conversationId: string) {
  const queryClient = useQueryClient();
  return (draft: FeedbackDraftView) =>
    queryClient.setQueryData<FeedbackDraftView[]>(
      feedbackDraftKeys.conversation(conversationId),
      (drafts = []) =>
        drafts.some((d) => d.id === draft.id)
          ? drafts.map((d) => (d.id === draft.id ? draft : d))
          : [...drafts, draft],
    );
}

/** "Report a problem" on an answer, or on a failed turn's notice (`messageId` absent). */
export function useReportProblem(conversationId: string) {
  const { csrfToken } = useSession();
  const remember = useRemember(conversationId);
  return useMutation({
    mutationFn: async (input: { messageId?: string | undefined; failed?: boolean }) =>
      (await ok(
        await api.api.feedback.drafts.$post(
          {
            json: {
              conversationId,
              ...(input.messageId !== undefined && { messageId: input.messageId }),
              ...(input.failed && { failed: true as const }),
            },
          },
          csrf(csrfToken),
        ),
      )) as FeedbackDraftView,
    onSuccess: remember,
  });
}

/** The user's Send, from the card (as drafted) or from the dialog (edited, groups unticked). */
export function useSendDraft(draft: FeedbackDraftView) {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const remember = useRemember(draft.conversationId);
  return useMutation({
    mutationFn: async (body: SendFeedbackDraftRequest): Promise<SendFeedbackResponse> => {
      const res = await api.api.feedback.drafts[":id"].send.$post(
        { param: { id: draft.id }, json: body },
        csrf(csrfToken),
      );
      if ((res.status as number) === 429) {
        const seconds = Number(res.headers.get("retry-after"));
        throw new FeedbackThrottled(
          new Date(Date.now() + (Number.isFinite(seconds) && seconds > 0 ? seconds : 3600) * 1000),
        );
      }
      if (!res.ok) {
        const error = await apiError(res);
        if (error.code === "feedback_disabled") {
          // Turned off since /api/me was read: hide the entry points, as the menu send does.
          void queryClient.invalidateQueries({ queryKey: ["me"] });
        }
        throw error;
      }
      return (await res.json()) as SendFeedbackResponse;
    },
    onSuccess: () => {
      remember({ ...draft, status: "sent" });
      return queryClient.invalidateQueries({ queryKey: feedbackKeys.mine });
    },
  });
}

/** Dismiss, then the optional reason tapped after it (the same request, with a reason). */
export function useDismissDraft(draft: FeedbackDraftView) {
  const { csrfToken } = useSession();
  const remember = useRemember(draft.conversationId);
  const post = async (reason?: DismissalReason) =>
    noContent(
      await api.api.feedback.drafts[":id"].dismiss.$post(
        { param: { id: draft.id }, json: reason ? { reason } : {} },
        csrf(csrfToken),
      ),
    );
  return {
    dismiss: useMutation({
      mutationFn: () => post(),
      onSuccess: () => remember({ ...draft, status: "dismissed" }),
    }),
    reason: (reason: DismissalReason) => post(reason),
  };
}
